import React, { useState } from 'react';
import { Box, Container, Paper, Typography, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// --- DADOS DE EXEMPLO PARA CADA TIPO DE COMPETÊNCIA ---

// 1. Dados para Competências Gerenciais
const managerialData = [
  { name: 'Liderança', media: 4.1 },
  { name: 'Gestão de Pessoas', media: 3.9 },
  { name: 'Visão Estratégica', media: 4.3 },
  { name: 'Tomada de Decisão', media: 4.0 },
  { name: 'Negociação', media: 3.7 },
];

// 2. Dados para Competências Corporativas
const corporateData = [
  { name: 'Comunicação', media: 4.2 },
  { name: 'Trabalho em Equipe', media: 3.8 },
  { name: 'Ética Profissional', media: 4.8 },
  { name: 'Foco no Cliente', media: 4.4 },
  { name: 'Inovação', media: 4.5 },
  { name: 'Adaptabilidade', media: 4.0 },
];

// 3. Dados para Competências Técnicas
const technicalData = [
  { name: 'Análise de Dados', media: 3.7 },
  { name: 'Dev. de Software', media: 4.0 },
  { name: 'Design UX/UI', media: 3.5 },
  { name: 'Gestão de Projetos', media: 4.1 },
  { name: 'Segurança da Info.', media: 3.9 },
];

// Mapeamento dos dados para facilitar a seleção
const competencyMap = {
  corporate: {
    data: corporateData,
    title: 'Média de Competências Corporativas',
    name: 'Média Corporativa',
    color: '#F97316', // Laranja
  },
  managerial: {
    data: managerialData,
    title: 'Média de Competências Gerenciais',
    name: 'Média Gerencial',
    color: '#3B82F6', // Azul
  },
  technical: {
    data: technicalData,
    title: 'Média de Competências Técnicas',
    name: 'Média Técnica',
    color: '#10B981', // Verde
  },
};

const ChartsPage = () => {
  // Estado para controlar o tipo de competência selecionado
  const [selectedType, setSelectedType] = useState('corporate');

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  // Obtém os dados do gráfico com base na seleção
  const currentChartData = competencyMap[selectedType];

  // Componente customizado para as legendas do Eixo X que quebra o texto
  const CustomizedAxisTick = ({ x, y, payload }) => {
    const label = payload.value;
    // Divide legendas longas em duas linhas para melhor legibilidade
    if (label.length > 10 && label.includes(' ')) {
        const words = label.split(' ');
        const midPoint = Math.ceil(words.length / 2);
        const line1 = words.slice(0, midPoint).join(' ');
        const line2 = words.slice(midPoint).join(' ');
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={10} textAnchor="middle" fill="#6B7280" fontSize={12}>
                    <tspan x={0} dy="0.6em">{line1}</tspan>
                    <tspan x={0} dy="1.2em">{line2}</tspan>
                </text>
            </g>
        );
    }
    // Renderiza legendas curtas em uma única linha
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="middle" fill="#6B7280" fontSize={12}>
                {label}
            </text>
        </g>
    );
  };


  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: '800', color: '#111827' }}>
          Análise de Competências
        </Typography>
        <Typography variant="body1" sx={{ color: '#6B7280' }}>
          Selecione um tipo de competência para visualizar a média de desempenho.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: '20px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
          <Grid item>
            <Typography variant="h6" component="h2" sx={{ color: '#1F2937', fontWeight: '700' }}>
              Visualizando:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="competency-select-label">Tipo de Competência</InputLabel>
              <Select
                labelId="competency-select-label"
                id="competency-select"
                value={selectedType}
                label="Tipo de Competência"
                onChange={handleTypeChange}
              >
                <MenuItem value="corporate">Competências Corporativas</MenuItem>
                <MenuItem value="managerial">Competências Gerenciais</MenuItem>
                <MenuItem value="technical">Competências Técnicas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ height: '60vh', minHeight: 500 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={currentChartData.data}
              margin={{ top: 10, right: 30, left: 0, bottom: 30 }} // Aumenta a margem inferior
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis 
                dataKey="name" 
                interval={0} 
                tick={<CustomizedAxisTick />} 
                height={60} 
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                domain={[0, 5]} 
                tick={{ fill: '#6B7280' }} 
                stroke="#D1D5DB" 
                axisLine={false} 
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                contentStyle={{
                  borderRadius: '12px',
                  borderColor: '#E5E7EB',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '30px' }} />
              <Bar
                dataKey="media"
                name={currentChartData.name}
                fill={currentChartData.color}
                radius={[8, 8, 0, 0]}
                maxBarSize={80}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Container>
  );
};

export default ChartsPage;
